class TrCountUp extends HTMLElement {
	attributes = {
		duration: 'data-tr-count-up-duration',
		value: 'data-tr-count-up-value',
	};

	stateClasses = {
		isVisible: 'is-visible',
	};

	observer = null;
	animationFrameId = null;

	connectedCallback() {
		this.valueElement = this;
		this.endValue = this.getEndValue();
		this.duration = this.getDuration();
		this.startValue = 0;
		this.hasAnimated = false;

		if (!this.valueElement) return;

		this.setAttribute(this.attributes.value, this.endValue.raw);

		this.observe();
	}

	disconnectedCallback() {
		this.destroy();
	}

	getEndValue() {
		const value = this.getAttribute(this.attributes.value) ?? this.textContent ?? '';
		const match = value.replace(/\s+/g, ' ').match(/-?\d+(?:[.,]\d+)?/);

		if (!match) {
			return {
				raw: value,
				number: 0,
				prefix: '',
				suffix: value,
				decimalPlaces: 0,
			};
		}

		const numberText = match[0];
		const numericValue = Number(numberText.replace(',', '.'));
		const prefix = value.slice(0, match.index);
		const suffix = value.slice(match.index + numberText.length);
		const decimalPlaces = numberText.includes('.') || numberText.includes(',') ? numberText.split(/[.,]/)[1].length : 0;

		return {
			raw: value,
			number: Number.isNaN(numericValue) ? 0 : numericValue,
			prefix,
			suffix,
			decimalPlaces,
		};
	}

	getDuration() {
		const duration = Number(this.getAttribute(this.attributes.duration));
		return Number.isFinite(duration) && duration > 0 ? duration : 1200;
	}

	observe() {
		if (!('IntersectionObserver' in window)) {
			this.start();
			return;
		}

		this.observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0]?.isIntersecting) return;
				this.start();
				this.observer?.disconnect();
				this.observer = null;
			},
			{ threshold: 0.35 }
		);

		this.observer.observe(this);
	}

	start() {
		if (this.hasAnimated) return;
		this.hasAnimated = true;

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			this.render(this.endValue.number);
			return;
		}

		const startTime = performance.now();

		const animate = (currentTime) => {
			const progress = Math.min((currentTime - startTime) / this.duration, 1);
			const easedProgress = 1 - Math.pow(1 - progress, 3);
			const currentValue = this.startValue + (this.endValue.number - this.startValue) * easedProgress;

			this.render(currentValue);

			if (progress < 1) {
				this.animationFrameId = window.requestAnimationFrame(animate);
			}
		};

		this.animationFrameId = window.requestAnimationFrame(animate);
	}

	render(currentValue) {
		const formattedValue = this.formatValue(currentValue);
		this.valueElement.textContent = `${this.endValue.prefix}${formattedValue}${this.endValue.suffix}`;
	}

	formatValue(value) {
		const roundedValue = this.endValue.decimalPlaces > 0 ? value.toFixed(this.endValue.decimalPlaces) : Math.round(value).toString();
		return roundedValue;
	}

	destroy() {
		this.observer?.disconnect();
		this.observer = null;

		if (this.animationFrameId) {
			window.cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}
	}
}

if (!customElements.get('tr-count-up')) {
	customElements.define('tr-count-up', TrCountUp);
}
